import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { submit3ds, type CardFields } from '../lib/payment';
import { initialTurnstileToken, turnstileConfigured } from '../lib/turnstile';
import { Turnstile } from './Turnstile';

/**
 * Collects card details and initiates a 3DS payment (deposit or membership). On submit the
 * browser navigates to the bank's 3DS page (see `submit3ds`); on a rejection the dialog stays
 * open and shows the error. `extraBody` carries endpoint-specific fields (e.g. `membershipId`).
 *
 * **These fields are in our own origin, so the card passes through our JavaScript and our
 * Worker — which puts both in PCI DSS scope** (28.08.2026 audit K-04; `docs/legal/README.md`
 * gap #6). Closing that means the fields coming from the provider's hosted page or hosted
 * fields instead, which amends [ADR 0011](../../../../docs/adr/0011-nestpay-fetch-client.md)
 * and waits on the acquirer. The `autocomplete` hints and the masked CVV below are ordinary
 * form hygiene — they fix shoulder-surfing and browser autofill, and **change nothing about
 * PCI scope**. Do not read them as the fix.
 */
export function PaymentDialog({
  open,
  onOpenChange,
  title,
  path,
  extraBody,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  path: string;
  extraBody?: Record<string, unknown>;
}) {
  const [card, setCard] = useState<CardFields>({ holder: '', number: '', expiry: '', cvv: '' });
  const [turnstileToken, setTurnstileToken] = useState(initialTurnstileToken());
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const onTurnstileToken = useCallback((tk: string) => setTurnstileToken(tk), []);

  // Reset the widget whenever the dialog opens/closes: closing unmounts Turnstile (its token is
  // consumed/void), so a reopened dialog must start from a fresh, unsubmitted token.
  useEffect(() => {
    setTurnstileToken(initialTurnstileToken());
    setTurnstileReset((n) => n + 1);
  }, [open]);

  const set = (k: keyof CardFields) => (e: { target: { value: string } }) =>
    setCard((c) => ({ ...c, [k]: e.target.value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await submit3ds(path, { card, turnstileToken, ...extraBody });
      // On success the document is replaced by the bank form — nothing runs after this.
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
      // The Turnstile token was consumed by the (failed) attempt — reset for a fresh one.
      setTurnstileToken(initialTurnstileToken());
      setTurnstileReset((n) => n + 1);
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="holder">{t('bidder.deposit.holder')}</Label>
            <Input
              id="holder"
              autoComplete="cc-name"
              spellCheck={false}
              value={card.holder}
              onChange={set('holder')}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="number">{t('bidder.deposit.number')}</Label>
            <Input
              id="number"
              inputMode="numeric"
              autoComplete="cc-number"
              spellCheck={false}
              value={card.number}
              onChange={set('number')}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="expiry">{t('bidder.deposit.expiry')}</Label>
              <Input
                id="expiry"
                placeholder="MM/YY"
                inputMode="numeric"
                autoComplete="cc-exp"
                spellCheck={false}
                value={card.expiry}
                onChange={set('expiry')}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cvv">{t('bidder.deposit.cvv')}</Label>
              {/* `type="password"`: the CVV is the one field that is worth nothing to the
                  bidder on screen and everything to whoever is standing behind them. */}
              <Input
                id="cvv"
                type="password"
                inputMode="numeric"
                autoComplete="cc-csc"
                spellCheck={false}
                value={card.cvv}
                onChange={set('cvv')}
                required
              />
            </div>
          </div>
          <Turnstile onToken={onTurnstileToken} resetKey={turnstileReset} />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button
            type="submit"
            className="w-full"
            disabled={busy || (turnstileConfigured && !turnstileToken)}
          >
            {busy ? t('common.loading') : t('bidder.deposit.submit')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
